/**
 * Resolves full row data for a click target inside a DataTable (button, icon, etc.).
 * @param {any} instance - DataTables API instance
 * @param {JQuery} $target
 * @returns {*|undefined}
 */
export function resolveRowDataFromElement(instance, $target) {
    const $cell = $target.closest('td');
    if ($cell.length) {
        const cell = instance.cell($cell);
        if (cell.any()) {
            return instance.row(cell.index().row).data();
        }
    }

    const $tr = $target.closest('tr');
    if (!$tr.length) return undefined;

    let row = instance.row($tr[0]);
    if (row.any()) return row.data();

    if ($tr.hasClass('child')) {
        const $parent = $tr.prev('tr');
        row = instance.row($parent[0]);
        if (row.any()) return row.data();
    }

    return undefined;
}

/**
 * Resolves a DataTables column API handle and config index from a name or index.
 * @param {any} adapter
 * @param {string|number} nameOrIndex
 * @returns {{ colApi: any, colIdx: number, config: any } | null}
 */
export function resolveColumn(adapter, nameOrIndex) {
    if (nameOrIndex == null) return null;

    let colIdx = typeof nameOrIndex === 'number' ? nameOrIndex : null;
    let colApi = adapter._instance.column(nameOrIndex);

    if (!colApi.length && typeof nameOrIndex === 'string') {
        colIdx = adapter._config.columns.findIndex((c) => c.data === nameOrIndex);
        if (colIdx !== -1) {
            colApi = adapter._instance.column(colIdx);
        }
    }

    if (!colApi.length) return null;

    if (colIdx === null || colIdx < 0) {
        colIdx = colApi.index();
    }

    const config = adapter._config.columns[colIdx] || {};
    return { colApi, colIdx, config };
}

/**
 * @param {any} adapter
 * @param {string|number} nameOrIndex
 * @returns {object | null}
 */
export function createColumnAccessor(adapter, nameOrIndex) {
    const resolved = resolveColumn(adapter, nameOrIndex);
    if (!resolved) return null;

    const { colApi, colIdx, config } = resolved;
    const key = config.data != null && config.data !== '' ? config.data : colIdx;
    const fieldKey = config.data;

    return {
        index: colIdx,
        data: config.data,
        title: config.title,
        config,
        get isVisible() {
            return colApi.visible();
        },
        /**
         * @param {number} rowIndex
         * @returns {{ data: *, row: object|undefined, node: HTMLElement|null }}
         */
        getRow(rowIndex) {
            const rowApi = adapter._instance.row(rowIndex);
            return {
                get data() {
                    if (!rowApi.any()) return undefined;
                    const rowData = rowApi.data();
                    if (fieldKey != null && fieldKey !== '') {
                        return rowData[fieldKey];
                    }
                    return rowData;
                },
                get row() {
                    return rowApi.any() ? rowApi.data() : undefined;
                },
                get node() {
                    return rowApi.any() ? rowApi.node() : null;
                }
            };
        },
        show() {
            adapter.setColumnVisibility(key, true);
            return this;
        },
        hide() {
            adapter.setColumnVisibility(key, false);
            return this;
        },
        toggle() {
            adapter.toggleColumn(key);
            return this;
        }
    };
}

/**
 * @param {any[]} columns
 * @returns {Record<string, string>}
 */
export function buildColumnKeyMap(columns = []) {
    const map = Object.create(null);
    columns.forEach((col) => {
        if (col.data != null && col.data !== '') {
            map[col.data] = col.data;
        }
    });
    return map;
}
